import React from 'react';

import { FilterContext } from '../../Context/FilterContext';
import Button from '../../Form/Button/Button';
import { AccountOnlineAnalysis, AccountSkinAnalysis } from '../../MyLom/Account/EditAccount/Types';
import { SkinAnalysisSectionIdentifier } from '../../Types';
import { formatDate, isSkinAnalysis } from '../../Utils';
import { PhoneInfo } from './PhoneInfo/PhoneInfo';
import SimpleLink from '../../Form/SimpleLink/SimpleLink';

import '../SkinAnalysisSection.scss';

interface Props {
    skinAnalysisSectionIdentifier: SkinAnalysisSectionIdentifier;
    skinAnalyses: Array<AccountSkinAnalysis | AccountOnlineAnalysis>;
    useReactRouterLink: boolean;
    toggleVisibility?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SkinAnalysisTable: React.FunctionComponent<Props> = (props) => {
    const { activeSkinAnalysis, updateActiveSkinanalysis, onlineAnalysis, updateOnlineAnalysis } =
        React.useContext(FilterContext);
    const handleAnalysisClick = (analysis: AccountSkinAnalysis | AccountOnlineAnalysis) => {
        if (isSkinAnalysis(analysis)) {
            if (analysis.id === activeSkinAnalysis) {
                return;
            }

            updateActiveSkinanalysis(analysis.id);
        } else {
            updateOnlineAnalysis(analysis);
        }
    };

    return (
        <table className="skin-analysis__table">
            <tbody>
                {props.skinAnalyses.map((analysis) => (
                    <tr
                        key={analysis.id}
                        className={
                            'skin-analysis__entry' +
                            (activeSkinAnalysis === analysis.id ? ' skin-analysis__entry--active' : '')
                        }
                    >
                        <td className="skin-analysis__type-column">
                            {isSkinAnalysis(analysis) ? (
                                <>
                                    {getAnalysisLabel(analysis)}
                                    {analysis.byPhone ? <PhoneInfo /> : ''}
                                </>
                            ) : (
                                <>Online-Hautbild-Analyse</>
                            )}
                        </td>
                        <td className="skin-analysis__date-column">
                            {formatDate(new Date(analysis.creationDateTime))}
                        </td>
                        <td className="skin-analysis__name-column">
                            {isSkinAnalysis(analysis) ? (
                                <>
                                    <div
                                        className="skin-analysis__consultant-image"
                                        aria-label={analysis.consultant.firstName + ' ' + analysis.consultant.lastName}
                                        data-balloon-pos="right"
                                    >
                                        <img src={analysis.consultant.image || ''} />
                                    </div>
                                    <span>
                                        {analysis.consultant.firstName} {analysis.consultant.lastName}
                                    </span>
                                </>
                            ) : null}
                        </td>
                        <td className="skin-analysis__link-column">
                            {isSkinAnalysis(analysis) && analysis.nextGenMeetingId ? (
                                <SimpleLink
                                    link={'/mylom/termine/zusammenfassung/' + analysis.nextGenMeetingId}
                                    text="Ergebnisse anzeigen"
                                />
                            ) : null}
                        </td>
                        <td className="skin-analysis__selection-column">
                            {!(isSkinAnalysis(analysis) && analysis.nextGenMeetingId) ? (
                                <Button
                                    icon={null}
                                    primary={
                                        activeSkinAnalysis === analysis.id ||
                                        (onlineAnalysis?.id !== undefined && onlineAnalysis?.id === analysis.id)
                                    }
                                    type="button"
                                    onClick={() => handleAnalysisClick(analysis)}
                                    dataSpacePreserve="wählen"
                                >
                                    {activeSkinAnalysis === analysis.id ||
                                    (onlineAnalysis?.id !== undefined && onlineAnalysis?.id === analysis.id)
                                        ? 'aktiv'
                                        : 'wählen'}
                                </Button>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export function getAnalysisLabel(analysis: AccountSkinAnalysis): string {
    if (analysis.type === 'consultant') {
        return 'Fachberater Hautbild-Analyse';
    }

    // FIXME: Why is there a label in the backend response we do not want to use?
    if (analysis.type === 'meeting' && analysis.nextGenMeetingId === null) {
        return 'Ersttermin Haut';
    }

    // remove that from title string since date is separate column
    return analysis.label.replace(/am.*$/, '');
}

export default SkinAnalysisTable;
